/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */ import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import FormData from 'form-data';

import { ApiError } from './ApiError';
import type { ApiRequestOptions } from './ApiRequestOptions';
import type { ApiResult } from './ApiResult';
import { CancelablePromise } from './CancelablePromise';
import type { OnCancel } from './CancelablePromise';
import type { OpenAPIConfig } from './OpenAPI';
import { OpenAPI } from './OpenAPI';

const isDefined = <T>(
  value: T | null | undefined
): value is Exclude<T, null | undefined> => {
  return value !== undefined && value !== null;
};

const isString = (value: any): value is string => {
  return typeof value === 'string';
};

const isStringWithValue = (value: any): value is string => {
  return isString(value) && value !== '';
};

const isBlob = (value: any): value is Blob => {
  return (
    typeof value === 'object' &&
    typeof value.type === 'string' &&
    typeof value.stream === 'function' &&
    typeof value.arrayBuffer === 'function' &&
    typeof value.constructor === 'function' &&
    typeof value.constructor.name === 'string' &&
    /^(Blob|File)$/.test(value.constructor.name) &&
    /^(Blob|File)$/.test(value[Symbol.toStringTag])
  );
};

const isFormData = (value: any): value is FormData => {
  return value instanceof FormData;
};

const isSuccess = (status: number): boolean => {
  return status >= 200 && status < 300;
};

const base64 = (str: string): string => {
  try {
    return btoa(str);
  } catch (err) {
    // @ts-ignore
    return Buffer.from(str).toString('base64');
  }
};

const getQueryString = (params: Record<string, any>): string => {
  const qs: string[] = [];

  const append = (key: string, value: any) => {
    qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };

  const process = (key: string, value: any) => {
    if (isDefined(value)) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          process(key, v);
        });
      } else if (typeof value === 'object') {
        Object.entries(value).forEach(([k, v]) => {
          process(`${key}[${k}]`, v);
        });
      } else {
        append(key, value);
      }
    }
  };

  Object.entries(params).forEach(([key, value]) => {
    process(key, value);
  });

  if (qs.length > 0) {
    return `?${qs.join('&')}`;
  }

  return '';
};

const getUrl = (config: OpenAPIConfig, options: ApiRequestOptions): string => {
  const encoder = config.ENCODE_PATH || encodeURI;

  const path = options.url.replace('{api-version}', config.VERSION);
  // .replace(/{(.*?)}/g, (substring: string, group: string) => {
  //   if (options.path?.hasOwnProperty(group)) {
  //     return encoder(String(options.path[group]));
  //   }
  //   return substring;
  // });

  const url = `${config.BASE}${path}`;
  if (options.query) {
    return `${url}${getQueryString(options.query)}`;
  }
  return url;
};

const getFormData = (options: ApiRequestOptions): FormData | undefined => {
  if (options.formData) {
    const formData = new FormData();

    const process = (key: string, value: any) => {
      if (isString(value) || isBlob(value)) {
        formData.append(key, value);
      } else {
        formData.append(key, JSON.stringify(value));
      }
    };

    Object.entries(options.formData)
      .filter(([_, value]) => isDefined(value))
      .forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => process(key, v));
        } else {
          process(key, value);
        }
      });

    return formData;
  }
  return undefined;
};

type Resolver<T> = (options: ApiRequestOptions) => Promise<T>;

const resolve = async <T>(
  options: ApiRequestOptions,
  resolver?: T | Resolver<T>
): Promise<T | undefined> => {
  if (typeof resolver === 'function') {
    return (resolver as Resolver<T>)(options);
  }
  return resolver;
};

export const createUUID = (
  header: string,
  saveAsCookie = true,
  saveLocally = true
): string => {
  if (typeof window === 'undefined') return '';
  const uuid = uuidv4();
  saveAsCookie && Cookies.set(header, uuid);
  saveLocally && localStorage.setItem(header, uuid);
  return uuid;
};

const getHeaders = async (options: ApiRequestOptions) => {
  const { cookies, headers: initialHeaders = {} } = options;
  const token =
    initialHeaders.token ||
    decodeURI(cookies?.token ?? '') ||
    (typeof window !== 'undefined' && localStorage.getItem('token')) ||
    createUUID('token', false, false);
  const sessionId =
    cookies?.sessionId ||
    (typeof window !== 'undefined' && localStorage.getItem('sessionId')) ||
    createUUID('sessionId', false, false);
  const userId =
    cookies?.userId ||
    (typeof window !== 'undefined' && localStorage.getItem('userId'));

  const defaultHeaders = await resolve(options, OpenAPI.HEADERS);
  const headers: Record<string, unknown> = {
    Accept: 'application/json',
    ...defaultHeaders,
    ...options.headers,
  };

  if (token && !options.headers?.authorization) {
    if (token.startsWith('Bearer')) {
      headers['authorization'] = token;
    } else {
      headers['authorization'] = `Bearer ${token}`;
    }
    headers['token'] = token;
  }

  // correlationId and sessionId might be already set
  if (!options.headers?.correlationId) headers['correlationId'] = uuidv4();
  if (!options.headers?.sessionId) headers['sessionId'] = sessionId;

  headers['userId'] = Number(userId) || 165;

  headers['languageId'] = 1055;

  if (options.body) {
    if (isBlob(options.body)) {
      headers['Content-Type'] = options.body.type || 'application/octet-stream';
    } else if (isString(options.body)) {
      headers['Content-Type'] = 'text/plain';
    } else {
      headers['Content-Type'] = 'application/json';
    }
  }
  return headers as unknown as Record<string, string>;
};

const getRequestBody = (options: ApiRequestOptions): any => {
  if (options.body) {
    return options.body;
  }
  return undefined;
};

const sendRequest = async <T>(
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Record<string, string>,
  onCancel: OnCancel
): Promise<AxiosResponse<T>> => {
  const source = axios.CancelToken.source();

  const data = body ?? formData ?? undefined;
  const requestConfig: AxiosRequestConfig = {
    url,
    headers,
    data,
    method: options.method,
    withCredentials: config.WITH_CREDENTIALS,
    cancelToken: source.token,
  };

  onCancel(() => source.cancel('The user aborted a request.'));

  try {
    return await axios.request(requestConfig);
  } catch (error) {
    const axiosError = error as AxiosError<T>;
    if (axiosError.response) {
      return axiosError.response;
    }
    throw error;
  }
};

const getResponseHeader = (
  response: AxiosResponse<any>,
  responseHeader?: string
): string | undefined => {
  if (responseHeader) {
    const content = response.headers[responseHeader];
    if (isString(content)) {
      return content;
    }
  }
  return undefined;
};

const getResponseBody = (response: AxiosResponse<any>): any => {
  if (response.status !== 204) {
    return response.data;
  }
  return undefined;
};

function UnauthorizedController() {
  if (typeof window !== 'undefined') {
    window.location.href = '/uye-girisi';
  } else {
    console.log('SSR : giriş yapmanız gerekiyor');
    //TODO: req redirect to /uye-girisi
  }
}

const catchErrorCodes = (
  options: ApiRequestOptions,
  result: ApiResult
): void => {
  const errors: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout',
    ...options.errors,
  };

  const error = errors[result.status];

  if (errors[result.status] === 'Unauthorized') return UnauthorizedController();

  if (error) {
    throw new ApiError(result, error);
  }

  if (!result.ok) {
    throw new ApiError(result, 'Generic Error');
  }
};

/**
 * Request method
 * @param options The request options from the service
 * @returns CancelablePromise<T>
 * @throws ApiError
 */
export const request = withOpenApiConfig(OpenAPI);

/**
 * withOpenApiConfig method
 * @param options The request options from the service
 * @returns request function calling the API
 */
function withOpenApiConfig(config: OpenAPIConfig) {
  const request = <T>(options: ApiRequestOptions) => {
    return new CancelablePromise<T>(async (resolve, reject, onCancel) => {
      try {
        const url = getUrl(config, options);
        const formData = getFormData(options);
        const body = getRequestBody(options);
        const headers = await getHeaders(options);

        if (!onCancel.isCancelled) {
          const response = await sendRequest<T>(
            config,
            options,
            url,
            body,
            formData,
            headers,
            onCancel
          );
          const responseBody = await getResponseBody(response);
          const responseHeader = getResponseHeader(
            response,
            options.responseHeader
          );

          const result: ApiResult = {
            ok: isSuccess(response.status),
            status: response.status,
            statusText: response.statusText,
            body: responseHeader ?? responseBody,
          };

          catchErrorCodes(options, result);

          resolve(result.body);
        }
      } catch (error) {
        reject(error);
      }
    });
  };
  return request;
}
